// Valid consent types
export const CONSENT_TYPES = [
  'data_processing', // Required for service
  'marketing_emails', // Promotional communications
  'community_emails', // Event reminders, connection requests
  'analytics', // Usage tracking
  'third_party_sharing', // LinkedIn integration, etc.
] as const

export type ConsentType = (typeof CONSENT_TYPES)[number]

/**
 * Who wrote a consent row (`consent_records.source`, 20260925040000):
 * `onboarding` for the defaults saved when joining, `settings` for a choice the
 * member made in Settings → Privacy & Consent. Null only on rows the backfill
 * could not have seen (none after the migration).
 */
export type ConsentSource = 'onboarding' | 'settings'

export interface ConsentRecord {
  id: string
  user_id: string
  consent_type: ConsentType
  granted: boolean
  source?: ConsentSource | null
  granted_at: string | null
  revoked_at: string | null
  policy_version: string
  created_at: string
  updated_at: string
}

/**
 * Privacy policy version a consent row is written under
 * (`consent_records.policy_version`). 1.1 (2026-09-25): usage analytics, when
 * on, are tied to the member's account; 1.0 called them anonymous.
 */
export const CURRENT_POLICY_VERSION = '1.1'

/** The first policy version whose analytics wording says the data is tied to the account. */
const ANALYTICS_TIED_TO_ACCOUNT_SINCE = '1.1'

/** `major.minor` compare; a missing or unreadable version counts as the oldest. */
function policyVersionAtLeast(version: string | null | undefined, minimum: string): boolean {
  const parse = (v: string) => v.split('.').map((part) => Number.parseInt(part, 10) || 0)
  if (!version) return false
  const [major = 0, minor = 0] = parse(version)
  const [minMajor = 0, minMinor = 0] = parse(minimum)
  return major !== minMajor ? major > minMajor : minor >= minMinor
}

/**
 * The account's answer on usage analytics, what decides PostHog's mode for a
 * signed-in member (lib/analytics/consent.ts):
 *
 * - `granted`: a yes given under the wording that says the data is tied to the
 *   account (policy 1.1 or later), from Settings, the web cookie banner while
 *   signed in, or onboarding (its box starts unticked, so a yes there is an
 *   opt-in).
 * - `withdrawn`: the member turned analytics off in Settings (or declined on
 *   the banner while signed in). Any policy version: whoever refused anonymous
 *   analytics refused this too.
 * - `none`: no answer that counts. No row; onboarding's unticked box (not a
 *   decision, only a missing opt-in); or a yes given under policy 1.0, whose
 *   wording promised anonymous data and so can't cover data tied to a person.
 */
export type AccountAnalyticsConsent = 'granted' | 'withdrawn' | 'none'

export function accountAnalyticsConsent(
  record:
    | (Pick<ConsentRecord, 'granted' | 'source'> & { policy_version?: string | null })
    | null
    | undefined
): AccountAnalyticsConsent {
  if (!record) return 'none'
  if (record.granted === false) return record.source === 'settings' ? 'withdrawn' : 'none'
  if (record.granted === true) {
    return policyVersionAtLeast(record.policy_version, ANALYTICS_TIED_TO_ACCOUNT_SINCE)
      ? 'granted'
      : 'none'
  }
  return 'none'
}

/**
 * Whether an onboarding answer on an opt-in consent (marketing, analytics,
 * third-party sharing) may be written over the member's current row. Onboarding
 * runs again for an existing account (admin onboarding after being a member,
 * ALU-89), and it must not undo a choice the member already made:
 *
 * - no row: the answer is written, ticked or not;
 * - a row from Settings (`source = 'settings'`, including the analytics answer
 *   given on the cookie banner while signed in): kept, yes or no; Settings is
 *   where it changes;
 * - a row from an earlier onboarding: a ticked box is a fresh opt-in under the
 *   current policy and is written (it can turn a 1.0 analytics yes, which no
 *   longer counts, into one that does); an unticked box is not a decision and
 *   leaves the row as it was.
 */
export function onboardingAnswerApplies(
  current: Pick<ConsentRecord, 'granted' | 'source'> | null | undefined,
  granted: boolean
): boolean {
  if (!current) return true
  if (current.source === 'settings') return false
  return granted
}

export interface ConsentUpdate {
  type: ConsentType
  granted: boolean
}

/**
 * Community emails are part of membership: they stay on until the member turns
 * them off in Settings. An onboarding row saved as `false` (the old onboarding
 * checkbox started unticked) is not an opt-out.
 */
export function isCommunityEmailWithdrawn(
  record: Pick<ConsentRecord, 'granted' | 'source'> | null | undefined
): boolean {
  return !!record && record.granted === false && record.source === 'settings'
}

/**
 * What a consent actually is for this member, the value Settings shows and the
 * senders apply: community emails on unless withdrawn in Settings, data
 * processing always on, analytics only with a yes that counts
 * (`accountAnalyticsConsent`), everything else only with an explicit opt-in.
 */
export function effectiveConsent(
  type: ConsentType,
  record:
    | (Pick<ConsentRecord, 'granted' | 'source'> & { policy_version?: string | null })
    | null
    | undefined
): boolean {
  if (type === 'data_processing') return true
  if (type === 'community_emails') return !isCommunityEmailWithdrawn(record)
  if (type === 'analytics') return accountAnalyticsConsent(record) === 'granted'
  return record?.granted === true
}
